import { useEffect, useRef } from 'react';

import {
    SUPPORTED_AUDIO_FORMATS,
    SUPPORTED_IMAGE_FORMATS,
    SUPPORTED_VIDEO_FORMATS,
} from '@webapp/common/components/file-manager/lib';
import { HelpText } from '@webapp/common/components/help-text';
import { ImageWithGallery } from '@webapp/common/components/image-with-gallery';
import { CDN_URL } from '@webapp/common/conf';
import { hasExtension } from '@webapp/common/lib/utils';
import { QuestionType } from '@webapp/common/resources/survey';

import css from '@webapp/survey/src/components/questions/question.css';
import type { QuestionContext } from '@webapp/survey/src/machine/question-machine';
import { selectPreviousQuestionById } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

type Props = {
    question: QuestionContext;
    offset?: number;
};

export const AdditionalContent = ({ question, offset }: Props) => {
    const isInit = useRef(null);
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const { carryMediaForwardFromId } = question.params;
    const carryMediaForwardQuestion = useStateSelector((ctx) =>
        selectPreviousQuestionById(ctx, question.order, carryMediaForwardFromId),
    );

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        videoRef.current?.pause();
        audioRef.current?.pause();
    }, [offset]);

    let { additionalImage, additionalText, additionalVideo, additionalAudio } = question.params;

    if (carryMediaForwardFromId && carryMediaForwardQuestion) {
        let selectedAnswer;

        if (carryMediaForwardQuestion.type === QuestionType.AB_TESTING) {
            selectedAnswer = carryMediaForwardQuestion.answersActors.find(
                (a) => a.getSnapshot().context.id === carryMediaForwardQuestion.selectedAnswerId,
            );
        } else if (carryMediaForwardQuestion.type === QuestionType.SELECT_ONE_IMAGE) {
            selectedAnswer = carryMediaForwardQuestion.answersActors.find((a) => {
                const { response } = a.getSnapshot().context;

                return response.getSnapshot().context.value;
            });
        }

        if (selectedAnswer) {
            const answerContext = selectedAnswer.getSnapshot().context;
            const path = answerContext.file?.path || answerContext.videoLink;

            if (path) {
                additionalImage = hasExtension(path, SUPPORTED_IMAGE_FORMATS) ? path : undefined;
                additionalVideo = hasExtension(path, SUPPORTED_VIDEO_FORMATS) ? path : undefined;
                additionalAudio = hasExtension(path, SUPPORTED_AUDIO_FORMATS) ? path : undefined;
            }

            if (answerContext.textBlock) {
                additionalText = answerContext.textBlock;
                // todo удалить value[1]
            } else if (answerContext.value?.[1]) {
                additionalText = answerContext.value[1];
            } else if (answerContext.value?.[0]) {
                additionalText = answerContext.value[0];
            }
        }
    }

    if (!additionalImage && !additionalVideo && !additionalAudio && !additionalText) return null;

    return (
        <div className={css.additionalContent}>
            <HelpText className={css.helpText} value={additionalText} />
            {additionalImage && <ImageWithGallery imageUrl={additionalImage} />}
            {additionalVideo && (
                <video ref={videoRef} controls={true} src={CDN_URL + additionalVideo}>
                    Your browser does not support the video tag.
                </video>
            )}
            {additionalAudio && (
                <audio
                    ref={audioRef}
                    className={css.audio}
                    controls={true}
                    src={CDN_URL + additionalAudio}
                    controlsList='nodownload'
                ></audio>
            )}
        </div>
    );
};
